import { readonly, ref } from 'vue'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

/**
 * One follow-up the user may take from the toast itself, rendered as a button
 * beside the message.
 *
 * It exists for undo (#3694). "Verwijderen uit mijn agenda" acts on an item that
 * leaves the screen and never comes back into view on its own, so unless the
 * toast carries the way back a misclick is unrecoverable from the agenda — and
 * the alternative, a confirmation dialog on a routine tidy-up action, is
 * friction that trains people to click through.
 *
 * Deliberately one action, not a list: a toast is transient and unfocused, so
 * anything that needs a choice needs a dialog instead.
 */
export interface ToastAction {
  /** Button label. Localised copy — never a bare string. */
  label: string
  /** Invoked on activation. The toast is dismissed first, so `run` may raise its own. */
  run: () => void
}

export interface ToastOptions {
  /** Visual + semantic variant. Defaults to `info`. */
  variant?: ToastVariant
  /** Milliseconds before auto-dismiss. `0` keeps the toast until it is dismissed. */
  duration?: number
  /** Optional single follow-up, rendered as a button. See {@link ToastAction}. */
  action?: ToastAction
}

export interface Toast {
  id: number
  message: string
  variant: ToastVariant
  duration: number
  action?: ToastAction
}

/** Default auto-dismiss delay for a toast (#1501). */
export const TOAST_DEFAULT_DURATION = 3000

/** A duration of 0 means "stay until dismissed" — the default for errors. */
export const TOAST_PERSIST = 0

/**
 * What {@link show} returns when it enqueued nothing. Ids start at 1, so this
 * can never name a live toast, and `dismiss` on it is a no-op.
 */
export const TOAST_NOT_SHOWN = 0

// Module-level state: the queue is global, so a toast raised anywhere is
// rendered by the single ToastContainer mounted in App.vue. Timers live
// outside the reactive state — they are bookkeeping, not something to render.
const toasts = ref<Toast[]>([])
const timers = new Map<number, ReturnType<typeof setTimeout>>()
let nextId = 0

/** Removes a toast and cancels its pending auto-dismiss, if any. */
function dismiss(id: number): void {
  const timer = timers.get(id)
  if (timer !== undefined) {
    clearTimeout(timer)
    timers.delete(id)
  }
  toasts.value = toasts.value.filter((toast) => toast.id !== id)
}

/**
 * Runs a toast's {@link ToastAction} and removes the toast.
 *
 * Dismissal happens first, so an action that raises a toast of its own — undo
 * reporting that it failed — does not queue behind the one it replaces.
 */
function runAction(id: number): void {
  const toast = toasts.value.find((entry) => entry.id === id)
  dismiss(id)
  toast?.action?.run()
}

/** Removes every toast. */
function clear(): void {
  timers.forEach((timer) => clearTimeout(timer))
  timers.clear()
  toasts.value = []
}

/**
 * Enqueues a toast and returns its id, which `dismiss` accepts.
 *
 * Errors default to persisting: they report something the user has to act on,
 * so they must not disappear while the user is still reading. Every other
 * variant auto-dismisses after {@link TOAST_DEFAULT_DURATION}. Either default
 * is overridable per toast via `options.duration`.
 *
 * An empty message enqueues nothing and returns {@link TOAST_NOT_SHOWN}. A
 * toast with no text says nothing on its own, and it is how
 * `SUPPRESSED_ERROR_MESSAGE` silences a failure that is already being reported
 * elsewhere — the organisation write gate's refusal, which is answered by a
 * persistent banner rather than one toast per attempted save (#4445).
 */
function show(message: string, options: ToastOptions = {}): number {
  if (message === '') return TOAST_NOT_SHOWN

  const variant = options.variant ?? 'info'
  const duration =
    options.duration ?? (variant === 'error' ? TOAST_PERSIST : TOAST_DEFAULT_DURATION)
  const id = ++nextId

  // Newest first: the container renders the queue top-down, so prepending puts
  // the newest toast at the top of the stack.
  toasts.value = [{ id, message, variant, duration, action: options.action }, ...toasts.value]

  if (duration > 0) {
    timers.set(
      id,
      setTimeout(() => dismiss(id), duration)
    )
  }

  return id
}

/**
 * Global toast queue — the app's user-feedback channel (#1501).
 *
 * Toasts are rendered top-right, stacked newest-first, by the single
 * `ToastContainer` in `App.vue`, which covers all three portals
 * (behandelaar, mijn, admin) since they share one SPA shell.
 *
 * ```ts
 * const { success, error } = useToast()
 * success('Lid toegevoegd')
 * error(extractApiErrorMessage(err))          // stays until dismissed
 * success(message, { duration: 8000 })        // per-toast override
 * success(message, { action: { label: undoLabel, run: undo } })
 * ```
 */
export function useToast() {
  return {
    /** The live queue, newest first. Read-only; mutate through the functions below. */
    toasts: readonly(toasts),
    show,
    success: (message: string, options?: Omit<ToastOptions, 'variant'>) =>
      show(message, { ...options, variant: 'success' }),
    error: (message: string, options?: Omit<ToastOptions, 'variant'>) =>
      show(message, { ...options, variant: 'error' }),
    warning: (message: string, options?: Omit<ToastOptions, 'variant'>) =>
      show(message, { ...options, variant: 'warning' }),
    info: (message: string, options?: Omit<ToastOptions, 'variant'>) =>
      show(message, { ...options, variant: 'info' }),
    runAction,
    dismiss,
    clear,
  }
}
